import { useState } from 'react';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { Link } from 'react-router-dom';

import './login.css';

import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import { checkPasswordRequirement, validatePassword } from '../../../utils/password.util';

import Alert from '@mui/material/Alert';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BsArrowRepeat } from 'react-icons/bs';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userHasAuthenticated } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isPasswordChangeRequired, setIsPasswordChangeRequired] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get redirect path from query parameter
  const redirectPath = searchParams.get('redirect') || '/service-forms';

  function validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function validateForm() {
    return email.length > 0 && password.length > 0 && validateEmail(email);
  }

  function getErrorMessage(error: any): string {
    const errorMessage = error?.message || error?.toString() || '';
    const errorCode = error?.name || error?.code || '';

    // Handle specific Cognito error codes
    if (errorCode === 'NotAuthorizedException' || errorMessage.includes('Incorrect username or password')) {
      return 'Incorrect email or password. Please try again.';
    }
    if (errorCode === 'UserNotConfirmedException' || errorMessage.includes('User is not confirmed')) {
      return 'Please verify your email address before logging in.';
    }
    if (errorCode === 'UserNotFoundException' || errorMessage.includes('User does not exist')) {
      return 'Account not found. Please check your email address.';
    }
    if (errorCode === 'TooManyRequestsException' || errorMessage.includes('Too many requests')) {
      return 'Too many login attempts. Please try again later.';
    }
    if (errorCode === 'PasswordResetRequiredException' || errorMessage.includes('Password reset required')) {
      return 'Password reset required. Please contact support.';
    }
    if (errorMessage.includes('User is disabled') || errorMessage.includes('User account is disabled')) {
      return 'Your account has been disabled. Please contact support.';
    }
    if (errorMessage.includes('User account is locked') || errorMessage.includes('User is locked')) {
      return 'Your account is temporarily locked. Please try again later.';
    }
    if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch')) {
      return 'Network error. Please check your internet connection and try again.';
    }

    // Return the error message if it's user-friendly, otherwise return a generic message
    if (errorMessage && !errorMessage.includes('AWS') && !errorMessage.includes('Cognito')) {
      return errorMessage;
    }

    return 'An error occurred during sign in. Please try again.';
  }


  async function handlePasswordChange(event: any) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password requirements
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      setIsLoading(false);
      return;
    }

    try {
      await confirmSignIn({ challengeResponse: newPassword });
      userHasAuthenticated(true);
      navigate(redirectPath);
    } catch (e: any) {
      onError(e);
      setErrorMessage(getErrorMessage(e));
      setIsLoading(false);
    }
  }

  async function handleSubmit(event: any) {
    event.preventDefault();

    setIsLoading(true);
    setErrorMessage('');

    // Validate email format
    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      const signInResult = await signIn({ username: email, password });

      // Check if password change is required
      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setIsPasswordChangeRequired(true);
        setIsLoading(false);
      } else {
        userHasAuthenticated(true);
        navigate(redirectPath);
      }
    } catch (e: any) {
      onError(e);
      setErrorMessage(getErrorMessage(e));
      setIsLoading(false);
    }
  }

  return (
    <div id="login-section">
      <div id="bg"></div>
      <section id="login">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 mx-auto my-auto">
              <img alt="CCL" id="ccl-logo" src="/assets/images/collector-car-leaders-logo.png" />
            </div>
            <div className="col-12">
              {isPasswordChangeRequired ? (
                <form onSubmit={handlePasswordChange}>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errorMessage}
                    </Alert>
                  )}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please set your new password to continue.
                  </Alert>
                  <TextField
                    fullWidth
                    required
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    label="New Password"
                    autoComplete="new-password"
                    autoFocus
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 2 }}
                  />
                  {newPassword && (
                    <Box sx={{ mb: 2, mt: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        Password requirements:
                      </Typography>
                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'length') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least 8 characters</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'uppercase') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least one uppercase letter</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'lowercase') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least one lowercase letter</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'number') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least one number</span>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {checkPasswordRequirement(newPassword, 'special') ? (
                            <CheckCircleIcon sx={{ fontSize: '14px', color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: '14px', color: 'error.main', mr: 0.5 }} />
                          )}
                          <span>At least one special character</span>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    required
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm Password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 2 }}
                  />
                  {confirmPassword && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      {newPassword === confirmPassword ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: 'success.main' }}>
                          <CheckCircleIcon sx={{ fontSize: '14px', mr: 0.5 }} />
                          <span>Passwords match</span>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: 'error.main' }}>
                          <CancelIcon sx={{ fontSize: '14px', mr: 0.5 }} />
                          <span>Passwords do not match</span>
                        </Box>
                      )}
                    </Box>
                  )}
                  <button id="submit-btn" type="submit" disabled={!newPassword || !confirmPassword || isLoading}>
                    {isLoading && <BsArrowRepeat className="spinning" />}
                    Set Password
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmit}>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errorMessage}
                    </Alert>
                  )}
                  <TextField
                    fullWidth
                    required
                    id="email"
                    name="email"
                    type="email"
                    label="Email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    required
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ mt: 1, mb: 2, textAlign: 'right' }}>
                    <Link
                      to="/reset-password"
                      style={{ color: 'inherit', textDecoration: 'none', fontSize: '0.875rem' }}
                    >
                      Forgot Password?
                    </Link>
                  </Box>
                  <button id="submit-btn" type="submit" disabled={!validateForm()}>
                    {isLoading && <BsArrowRepeat className="spinning" />}
                    Login
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
